import { useEffect, useMemo, useState } from "react";
import {
  Card,
  Badge,
  Notice,
  Button,
  Icon,
  Select,
  SelectItem,
  SegmentedControl,
  Segment,
  ProgressBar,
  type BadgeTone,
} from "@conuti-das/prince-ui";
import { Flex, Stack, Spinner, princeIcon } from "@/ui-prince";
import {
  validatorApi,
  type Coverage,
  type ConditionRow,
  type ConditionStatus,
} from "@/api/validatorApi";
import { BundleSelector } from "./BundleSelector";
import { ValidatorSubNav } from "./ValidatorSubNav";

type StatusFilter = "all" | ConditionStatus;

const STATUS_LABEL: Record<ConditionStatus, string> = {
  implemented: "Implementiert",
  unimplemented: "Ohne Checker",
  format_not_evaluable: "Format n. eval.",
};

const STATUS_BADGE_TONE: Record<ConditionStatus, BadgeTone> = {
  implemented: "green",
  unimplemented: "red",
  format_not_evaluable: "orange",
};

function flatten(cov: Coverage): ConditionRow[] {
  const rows: ConditionRow[] = [];
  for (const f of cov.per_format) {
    for (const c of f.implemented_list ?? []) {
      rows.push({
        format: f.format,
        condition_key: c.condition_key,
        condition_text: c.condition_text,
        status: "implemented",
        impl_kind: c.impl_kind,
        impl_hint: c.impl_hint,
        impl_code_ref: c.impl_code_ref,
      });
    }
    for (const c of f.unimplemented) {
      rows.push({
        format: f.format,
        condition_key: c.condition_key,
        condition_text: c.condition_text,
        status: "unimplemented",
      });
    }
    for (const c of f.format_not_evaluable) {
      rows.push({
        format: f.format,
        condition_key: c.condition_key,
        condition_text: c.condition_text,
        status: "format_not_evaluable",
      });
    }
  }
  return rows;
}

function downloadCsv(rows: ConditionRow[], bundle: string) {
  const header = "format,condition_key,status,condition_text\n";
  const body = rows
    .map((r) => {
      const text = r.condition_text.replace(/"/g, '""');
      return `${r.format},${r.condition_key},${r.status},"${text}"`;
    })
    .join("\n");
  const blob = new Blob([header + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `conditions-${bundle}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function ConditionsOverview() {
  const [bundleId, setBundleId] = useState("");
  const [cov, setCov] = useState<Coverage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Filters
  const [formatFilter, setFormatFilter] = useState<string>("__all__");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<"format" | "condition_key" | "status">("format");

  useEffect(() => {
    if (!bundleId) return;
    setCov(null);
    setError(null);
    setBusy(true);
    validatorApi
      .getCoverage(bundleId)
      .then(setCov)
      .catch((e) => setError(String(e)))
      .finally(() => setBusy(false));
  }, [bundleId]);

  const allRows: ConditionRow[] = useMemo(() => (cov ? flatten(cov) : []), [cov]);

  const formats = useMemo(
    () => Array.from(new Set(allRows.map((r) => r.format))).sort(),
    [allRows],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = allRows;
    if (formatFilter !== "__all__") rows = rows.filter((r) => r.format === formatFilter);
    if (statusFilter !== "all") rows = rows.filter((r) => r.status === statusFilter);
    if (q) {
      rows = rows.filter(
        (r) =>
          r.condition_key.toLowerCase().includes(q) ||
          r.condition_text.toLowerCase().includes(q),
      );
    }
    // Sort
    rows = [...rows].sort((a, b) => {
      if (sortKey === "format") {
        const fc = a.format.localeCompare(b.format);
        if (fc !== 0) return fc;
        return Number(a.condition_key) - Number(b.condition_key) || a.condition_key.localeCompare(b.condition_key);
      }
      if (sortKey === "condition_key") {
        const n = Number(a.condition_key) - Number(b.condition_key);
        if (!Number.isNaN(n) && n !== 0) return n;
        return a.condition_key.localeCompare(b.condition_key);
      }
      // status
      return a.status.localeCompare(b.status);
    });
    return rows;
  }, [allRows, formatFilter, statusFilter, search, sortKey]);

  const counts = useMemo(() => {
    const c = { implemented: 0, unimplemented: 0, format_not_evaluable: 0 };
    for (const r of filteredRows) c[r.status]++;
    return c;
  }, [filteredRows]);

  const visibleTotal = filteredRows.length;
  const grandTotal = allRows.length;
  const pctImpl = visibleTotal === 0 ? 0 : Math.round((counts.implemented / visibleTotal) * 100);

  return (
    <div className="page">
      <div className="page-header">
        <h2 className="page-title">Conditions-Übersicht</h2>
        <div className="page-subtitle">
          Pflege-UI für AHB-Conditions pro Bundle. Zeigt welche Bedingungen Checker-Logik haben,
          welche per Default als erfüllt angenommen werden, und welche Format-Constraints nicht
          maschinell auswertbar sind.
        </div>
      </div>

      <ValidatorSubNav />

      <Card>
        <Stack style={{ padding: "var(--s-3) var(--s-4)", gap: "var(--s-3)" }}>
          <Flex style={{ gap: "var(--s-4)", flexWrap: "wrap", alignItems: "center" }}>
            <BundleSelector value={bundleId} onChange={setBundleId} />
          </Flex>
        </Stack>
      </Card>

      {error && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="negative">
            Coverage konnte nicht geladen werden: {error}
          </Notice>
        </div>
      )}

      {busy && (
        <div style={{ marginTop: "var(--s-3)", display: "flex", alignItems: "center", gap: "var(--s-2)" }}>
          <Spinner size="m" />
          <span style={{ color: "var(--sapContent_LabelColor)" }}>Lade Coverage ...</span>
        </div>
      )}

      {cov && (
        <>
          <Card style={{ marginTop: "var(--s-3)" }} title="Coverage-Status (Filter aktiv)">
            <div style={{ padding: "var(--s-3) var(--s-4)" }}>
              <div style={{ fontSize: "0.85rem", color: "var(--sapContent_LabelColor)", marginBottom: "var(--s-2)" }}>
                {visibleTotal} sichtbar von {grandTotal} Conditions in Formatversion {cov.formatversion}
              </div>
              <Stack style={{ gap: "var(--s-3)" }}>
                <ProgressBar
                  value={pctImpl}
                  label={`${pctImpl}% implementiert (in Auswahl)`}
                />
                <Flex style={{ gap: "var(--s-5)", flexWrap: "wrap" }}>
                  <Badge tone="green" variant="solid">
                    {counts.implemented} Implementiert
                  </Badge>
                  <Badge tone="red" variant="solid">
                    {counts.unimplemented} Ohne Checker
                  </Badge>
                  <Badge tone="orange" variant="solid">
                    {counts.format_not_evaluable} Format n. eval.
                  </Badge>
                </Flex>
              </Stack>
            </div>
          </Card>

          <Card style={{ marginTop: "var(--s-3)" }}>
            <Stack
              style={{ padding: "var(--s-3) var(--s-4)", gap: "var(--s-3)" }}
            >
              <Flex style={{ gap: "var(--s-4)", flexWrap: "wrap", alignItems: "center" }}>
                <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
                  <label htmlFor="filter-format" className="prn-field-label">Format</label>
                  <Select
                    id="filter-format"
                    selectedKey={formatFilter}
                    onSelectionChange={(key) => {
                      if (key != null) setFormatFilter(String(key));
                    }}
                    aria-label="Format filtern"
                  >
                    <SelectItem id="__all__">Alle Formate</SelectItem>
                    {formats.map((f) => (
                      <SelectItem key={f} id={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </Select>
                </Flex>

                <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
                  <span className="prn-field-label">Status</span>
                  <SegmentedControl
                    aria-label="Status-Filter"
                    selectedKeys={new Set([statusFilter])}
                    onSelectionChange={(keys) => {
                      const first = [...keys][0];
                      if (first != null) setStatusFilter(String(first) as StatusFilter);
                    }}
                  >
                    <Segment id="all">Alle</Segment>
                    <Segment id="implemented">Implementiert</Segment>
                    <Segment id="unimplemented">Ohne Checker</Segment>
                    <Segment id="format_not_evaluable">Format n. eval.</Segment>
                  </SegmentedControl>
                </Flex>

                <Flex style={{ alignItems: "center", gap: "var(--s-2)", flex: "1 1 240px" }}>
                  <label htmlFor="filter-search" className="prn-field-label">Suche</label>
                  <input
                    id="filter-search"
                    className="prn-input"
                    value={search}
                    onChange={(e) => setSearch(e.target.value ?? "")}
                    placeholder="condition_key oder Text ..."
                    style={{ flex: 1 }}
                  />
                </Flex>

                <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
                  <span className="prn-field-label">Sortierung</span>
                  <SegmentedControl
                    aria-label="Sortierung"
                    selectedKeys={new Set([sortKey])}
                    onSelectionChange={(keys) => {
                      const first = [...keys][0];
                      if (first != null) setSortKey(String(first) as typeof sortKey);
                    }}
                  >
                    <Segment id="format">Format</Segment>
                    <Segment id="condition_key">Key</Segment>
                    <Segment id="status">Status</Segment>
                  </SegmentedControl>
                </Flex>

                <Button
                  variant="plain"
                  isDisabled={filteredRows.length === 0}
                  onPress={() => downloadCsv(filteredRows, cov.formatversion)}
                >
                  <Icon name={princeIcon("download")} size={16} />
                  CSV-Export
                </Button>
              </Flex>

              <div style={{ maxHeight: 640, overflowY: "auto" }}>
                <table className="fiori-data-table" style={{ width: "100%" }}>
                  <thead style={{ position: "sticky", top: 0, background: "var(--sapList_HeaderBackground)" }}>
                    <tr>
                      <th style={{ width: 90 }}>Format</th>
                      <th style={{ width: 80 }}>Key</th>
                      <th style={{ width: 150 }}>Status</th>
                      <th>Bedingungstext</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={`${r.format}-${r.condition_key}`}>
                        <td
                          style={{
                            fontFamily: "var(--sapFontMonoFamily, monospace)",
                            fontSize: "0.85rem",
                          }}
                        >
                          {r.format}
                        </td>
                        <td
                          style={{
                            fontFamily: "var(--sapFontMonoFamily, monospace)",
                            fontSize: "0.85rem",
                          }}
                        >
                          [{r.condition_key}]
                        </td>
                        <td>
                          <Badge tone={STATUS_BADGE_TONE[r.status]}>
                            {STATUS_LABEL[r.status]}
                          </Badge>
                        </td>
                        <td style={{ fontSize: "0.9rem" }}>
                          {r.condition_text}
                          {r.status === "implemented" && (r.impl_hint || r.impl_kind) && (
                            <div
                              style={{
                                marginTop: "var(--s-1)",
                                fontSize: "0.78rem",
                                color: "var(--sapContent_LabelColor)",
                                fontFamily: "var(--sapFontMonoFamily, monospace)",
                              }}
                              title={r.impl_kind ? `Checker-Klasse: ${r.impl_kind}` : undefined}
                            >
                              ↳ {r.impl_hint || r.impl_kind}
                              {r.impl_code_ref ? `  ·  ${r.impl_code_ref}` : ""}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredRows.length === 0 && (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", padding: "var(--s-5)" }}>
                          <span style={{ color: "var(--sapContent_LabelColor)" }}>
                            Keine Conditions in der aktuellen Auswahl.
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Stack>
          </Card>
        </>
      )}
    </div>
  );
}
